// Drawer del copiloto Jido — reusa el panel lateral genérico (components/ui/drawer.tsx).
//
// Rediseño editorial: header con título display (font-serif) + línea de alcance
// (font-mono, tenant · rol · alcance), marca de agua kanji 自働 de fondo y ancho
// 460 (mockup). El contenido va sobre la marca de agua (relative z-10).

import type { JwtClaims, Role } from 'schemas';
import { Drawer, DrawerBody, DrawerHeader } from '../../components/ui/drawer';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import { useAuth } from '../../lib/auth/useAuth';
import { useVerComo, type VerComoState } from '../../lib/ver-como';
import { CopilotoChat } from './CopilotoChat';
import { MarcaAguaKanji } from './MarcaAguaKanji';

interface CopilotoDrawerProps {
  open: boolean;
  onClose: () => void;
}

// Solo estos 3 roles ven el copiloto (ver CopilotoFab / requireRole del backend).
const ROL_LABEL: Partial<Record<Role, string>> = {
  directivo: 'Directivo',
  coordinador: 'Coordinador',
  super_admin: 'Super Admin',
};

const ALCANCE_LABEL: Partial<Record<Role, string>> = {
  directivo: 'todas las plantas',
  coordinador: 'su planta',
  super_admin: 'todos los tenants',
};

/**
 * "Robotpack · Directivo · todas las plantas" — derivado del JWT. En modo
 * "Ver como" el backend responde con el scope del suplantado, así que la línea
 * lo dice: "Robotpack · Coordinador · Sopó · viendo como Geraldine …".
 */
function construirAlcance(claims: JwtClaims, verComo: VerComoState | null): string {
  // Defensivo: aunque el schema exige tenants_disponibles, la línea de alcance
  // NUNCA debe poder tirar el panel entero — si falta, degradamos a rol+alcance.
  const tenant =
    claims.tenants_disponibles?.find((t) => t.id === claims.tenant_id_activo)?.nombre ?? '';
  if (verComo && verComo.destino !== 'mantenimiento') {
    const rol = ROL_LABEL[verComo.destino] ?? verComo.destino;
    const alcance = verComo.plantaLabel ?? ALCANCE_LABEL[verComo.destino] ?? '';
    const persona = verComo.nombre ? `viendo como ${verComo.nombre}` : 'viendo como otra persona';
    return [tenant, rol, alcance, persona].filter(Boolean).join(' · ');
  }
  const rol = ROL_LABEL[claims.rol] ?? claims.rol;
  const alcance = ALCANCE_LABEL[claims.rol] ?? '';
  return [tenant, rol, alcance].filter(Boolean).join(' · ');
}

export function CopilotoDrawer({ open, onClose }: CopilotoDrawerProps) {
  const { state } = useAuth();
  const { estado: verComo } = useVerComo();
  const alcance = state.kind === 'signed_in' ? construirAlcance(state.claims, verComo) : null;

  return (
    <Drawer open={open} onClose={onClose} width={460}>
      <MarcaAguaKanji />

      <DrawerHeader className="relative z-10 flex-col items-start gap-1">
        <div className="flex w-full items-start justify-between gap-2">
          <h2 className="font-serif text-xl leading-tight text-fg">Jido — Agente de producción</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar copiloto">
            <Icon name="x" />
          </Button>
        </div>
        {alcance ? <p className="font-mono text-xs text-fg-secondary">{alcance}</p> : null}
      </DrawerHeader>

      <DrawerBody className="relative z-10 flex flex-1 flex-col">
        <CopilotoChat />
      </DrawerBody>
    </Drawer>
  );
}
